import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useUser } from 'contexts';
import ItemMasterForm from 'views/admin/masters/Pages/ItemMaster/ItemMasterForm';
import { useDisclosure } from '@chakra-ui/react';

const OPTION_HEIGHT = 40;
const MENU_MAX_HEIGHT = 300;
const BATCH_SIZE = 50;

const MultiSelectDropdown = ({
   label,
   options = [],
   value = [],
   onChange,
   placeholder = 'Select options...',
   isRequired = false,
   error,
   showAddButton = true,
   onAddNew,
   isLoading = false,
   darkMode = false,
   AddEditFormComponent,
   ItemFormComponent,
   formProps = {},
   ...props
}) => {
   const [isOpen, setIsOpen] = useState(false);
   const [menuIsOpen, setMenuIsOpen] = useState(false);
   const [isFocused, setIsFocused] = useState(false);
   const [addoption, setAddoption] = useState();
   const { onClose } = useDisclosure();
   const [searchTerm, setSearchTerm] = useState('');
   const [loadedCount, setLoadedCount] = useState(BATCH_SIZE);
   const [isFormOpen, setIsFormOpen] = useState(false);
   const [isEditMode, setIsEditMode] = useState(false);
   const [currentRow, setCurrentRow] = useState({});
   const [demoname, setDemoname] = useState('');
   const selectRef = useRef(null);
   const searchRef = useRef(null);
   const listRef = useRef(null);
   const buttonRef = useRef(null);
   const [highlightedIndex, setHighlightedIndex] = useState(0);
   const { user } = useUser();

   // Theme-based class generator
   const getThemeClass = (lightClass, darkClass) => {
      return darkMode ? darkClass : lightClass;
   };

   const useData = options.map((opt) => ({
      ...opt,
      value: opt.id,
      label: opt.label ?? opt.name,
   }));

   // Filter useData based on search term (with trimmed search)
   const filteredOptions = useMemo(() => {
      const trimmedSearchTerm = searchTerm.trim();

      if (!trimmedSearchTerm) return useData;

      return useData.filter(
         (option) =>
            option.label?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.value?.toString().toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.gstRegNo?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.panNo?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.mobile?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
            option.ownerName?.toLowerCase().includes(trimmedSearchTerm.toLowerCase()),
      );
   }, [useData, searchTerm]);

   // useData to display (with virtual scrolling)
   const displayOptions = useMemo(() => {
      return filteredOptions.slice(0, loadedCount);
   }, [filteredOptions, loadedCount]);

   // Select All option
   const selectAllOption = useMemo(
      () => ({
         value: 'SELECT_ALL',
         label: value.length === filteredOptions.length ? 'Deselect All' : 'Select All',
      }),
      [value.length, filteredOptions.length],
   );

   // Enhanced options with select all
   const enhancedOptions = useMemo(() => [selectAllOption, ...displayOptions], [selectAllOption, displayOptions]);

   const selectedOptions = useMemo(() => {
      return useData.filter((opt) => value.includes(opt.value));
   }, [value, useData]);

   // Handle select all/deselect all
   const handleSelectAll = useCallback(() => {
      if (value.length === filteredOptions.length) {
         onChange([]);
      } else {
         const allValues = filteredOptions.map((opt) => opt.value);
         onChange(allValues);
      }
   }, [value.length, filteredOptions, onChange]);

   // Handle individual option selection
   const handleOptionToggle = useCallback(
      (optionValue) => {
         if (optionValue === 'SELECT_ALL') {
            handleSelectAll();
            return;
         }

         const newValue = value.includes(optionValue)
            ? value.filter((v) => v !== optionValue)
            : [...value, optionValue];

         onChange(newValue);
      },
      [value, onChange, handleSelectAll],
   );

   const handleRemoveOption = useCallback(
      (optionValue, e) => {
         e.stopPropagation();
         const newValue = value.filter((v) => v !== optionValue);
         onChange(newValue);
      },
      [value, onChange],
   );

   const handleClearAll = useCallback(
      (e) => {
         e.stopPropagation();
         onChange([]);
      },
      [onChange],
   );

   // Load more items on scroll
   const handleScroll = useCallback(
      ({ scrollOffset, scrollUpdateWasRequested }) => {
         if (
            !scrollUpdateWasRequested &&
            loadedCount < filteredOptions.length &&
            scrollOffset > loadedCount * OPTION_HEIGHT - MENU_MAX_HEIGHT - 100
         ) {
            setLoadedCount((prev) => Math.min(prev + BATCH_SIZE, filteredOptions.length));
         }
      },
      [loadedCount, filteredOptions.length],
   );

   // Reset loaded count when search changes
   useEffect(() => {
      setLoadedCount(BATCH_SIZE);
   }, [searchTerm]);

   // Close dropdown when clicking outside
   useEffect(() => {
      const handleClickOutside = (event) => {
         if (selectRef.current && !selectRef.current.contains(event.target)) {
            setIsOpen(false);
            setSearchTerm('');
         }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, []);

   // Focus search when opening
   useEffect(() => {
      if (isOpen && searchRef.current) {
         setTimeout(() => searchRef.current?.focus(), 100);
      }
   }, [isOpen]);

   // Handle keyboard input on the button to open dropdown and start typing
   const handleButtonKeyDown = useCallback((e) => {
      // Allow alphanumeric keys and space to open dropdown and start typing
      const isTypingKey = /^[a-zA-Z0-9 ]$/.test(e.key);

      if (isTypingKey) {
         e.preventDefault();
         setIsOpen(true);
         setSearchTerm(e.key);
      } else if (e.key === 'Enter' || e.key === ' ') {
         // Space and Enter toggle the dropdown
         e.preventDefault();
         setIsOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
         setIsOpen(false);
         setSearchTerm('');
      }
   }, []);

   const handleAddNew = useCallback(
      (searchValue = '') => {
         const trimmedSearchValue = searchValue.trim();
         setDemoname(trimmedSearchValue);
         setIsEditMode(false);
         setCurrentRow({});
         setIsFormOpen(true);
         setIsOpen(false);
         setSearchTerm('');

         if (onAddNew) {
            onAddNew(trimmedSearchValue);
         }
      },
      [onAddNew],
   );

   const handleFormSave = useCallback(
      (formData) => {
         setIsFormOpen(false);

         const newOption = {
            ...formData,
            label: formData.name || formData.label,
            value: formData.id,
         };

         const newValue = [...value, newOption.value];
         onChange(newValue);

         setTimeout(() => {
            setIsOpen(true);
         }, 100);
      },
      [value, onChange],
   );

   const handleFormClose = useCallback(() => {
      setIsFormOpen(false);
      setTimeout(() => {
         setIsOpen(true);
      }, 100);
   }, []);

   const handleSave = useCallback(
      (formData) => {
         onClose();
         const newOption = { ...formData, label: formData.name, value: formData.id };
         setAddoption(newOption.value);
         onChange(newOption.value);
         setMenuIsOpen(true);
         setIsFocused(true);
      },
      [onChange, onClose],
   );

   const handleClose = useCallback(() => {
      setIsOpen(false);
      setMenuIsOpen(true);
   }, []);

   const Option = useCallback(
      ({ index, style, data }) => {
         const option = data[index];
         const isHighlighted = index === highlightedIndex;
         const isSelected =
            option.value === 'SELECT_ALL' ? value.length === filteredOptions.length : value.includes(option.value);
         const isSelectAll = option.value === 'SELECT_ALL';

         return (
            <div
               style={style}
               className={`flex items-center px-3 py-2 cursor-pointer transition-colors border ${isSelected
                  ? getThemeClass('bg-blue-50 border-blue-200', 'bg-blue-900 border-blue-700')
                  : getThemeClass('hover:bg-gray-50 border-transparent', 'hover:bg-gray-700 border-transparent')
                  } 
                  ${isSelectAll
                     ? getThemeClass('border-b border-gray-200 bg-gray-50', 'border-b border-gray-600 bg-gray-800')
                     : ''
                  }
                  ${isHighlighted ? getThemeClass("bg-blue-100", "dark:bg-blue-700") : ""}`}
               onMouseEnter={() => setHighlightedIndex(index)}
               onClick={() => handleOptionToggle(option.value)}>
               <div
                  className={`flex items-center justify-center w-4 h-4 border rounded ${isSelected
                     ? getThemeClass('bg-blue-600 border-blue-600', 'bg-blue-500 border-blue-500')
                     : getThemeClass('border-gray-300', 'border-gray-500')
                     }`}>
                  {isSelected && (
                     <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                     </svg>
                  )}
               </div>

               <span
                  className={`ml-3 text-sm truncate ${isSelectAll
                     ? getThemeClass('font-semibold text-gray-700', 'font-semibold text-gray-200')
                     : getThemeClass('text-gray-900', 'text-gray-100')
                     }`}>
                  {option.label}
               </span>
            </div>
         );
      },
      [value, filteredOptions.length, handleOptionToggle, darkMode],
   );

   useEffect(() => {
      if (isOpen) setHighlightedIndex(0);
   }, [isOpen]);

   const handleKeyNavigation = useCallback((e) => {
      if (!isOpen) return;

      if (e.key === "ArrowDown") {
         e.preventDefault();
         setHighlightedIndex((prev) =>
            Math.min(prev + 1, enhancedOptions.length - 1)
         );

         // Auto-scroll react-window
         listRef.current?.scrollToItem(highlightedIndex + 1);
      }

      if (e.key === "ArrowUp") {
         e.preventDefault();
         setHighlightedIndex((prev) => Math.max(prev - 1, 0));
         listRef.current?.scrollToItem(highlightedIndex - 1);
      }

      if (e.key === "Enter") {
         e.preventDefault();
         const option = enhancedOptions[highlightedIndex];
         if (option) handleOptionToggle(option.value);
      }

      if (e.key === "Escape") {
         setIsOpen(false);
      }
   }, [isOpen, highlightedIndex, enhancedOptions, handleOptionToggle]);

   return (
      <>
         <div className="relative w-full" ref={selectRef}>
            {/* Trigger Button */}
            <button
               ref={buttonRef}
               type="button"
               className={`w-full px-3 py-2 text-left border rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${getThemeClass(
                  'bg-white text-gray-900',
                  'bg-gray-800 text-white',
               )} ${error
                  ? getThemeClass('border-red-300 ring-1 ring-red-300', 'border-red-500 ring-1 ring-red-500')
                  : getThemeClass('border-gray-300 hover:border-gray-400', 'border-gray-600 hover:border-gray-500')
                  } ${isOpen
                     ? getThemeClass('border-blue-500 ring-2 ring-blue-200', 'border-blue-400 ring-2 ring-blue-800')
                     : ''
                  }`}
               onClick={() => setIsOpen(!isOpen)}
               onKeyDown={(e) => {
                  handleButtonKeyDown(e);      // existing behavior
                  handleKeyNavigation(e);      // NEW: up/down navigation
               }}>
               <div className="flex items-center justify-between">
                  <span
                     className={`truncate ${value.length === 0
                        ? getThemeClass('text-gray-500', 'text-gray-400')
                        : getThemeClass('text-gray-900', 'text-gray-100')
                        }`}>
                     {value.length === 0
                        ? placeholder
                        : value.length === 1
                           ? selectedOptions[0]?.label
                           : `${selectedOptions[0]?.label} + ${value.length - 1} more`}
                  </span>

                  <div className="flex items-center space-x-2">
                     {value.length > 0 && (
                        <button
                           type="button"
                           className={`p-1 rounded-full ${getThemeClass(
                              'hover:bg-gray-200 text-gray-500 hover:text-red-500',
                              'hover:bg-gray-700 text-gray-400 hover:text-red-400',
                           )} transition-colors`}
                           onClick={handleClearAll}
                           title="Clear all">
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth={2}
                                 d="M6 18L18 6M6 6l12 12"
                              />
                           </svg>
                        </button>
                     )}

                     <svg
                        className={`w-4 h-4 transition-transform ${getThemeClass('text-gray-400', 'text-gray-500')} ${isOpen ? 'rotate-180' : ''
                           }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                     </svg>
                  </div>
               </div>
            </button>

            {/* Error Message */}
            {error && <p className={`mt-1 text-sm ${getThemeClass('text-red-600', 'text-red-400')}`}>{error}</p>}

            {/* Dropdown Menu */}
            {isOpen && (
               <div
                  className={`absolute z-50 w-full mt-1 border rounded-lg shadow-lg ${getThemeClass(
                     'bg-white border-gray-200 shadow-lg',
                     'bg-gray-800 border-gray-600 shadow-xl',
                  )}`}>
                  {/* Search Input */}
                  <div className={`p-2 border-b ${getThemeClass('border-gray-200', 'border-gray-600')}`}>
                     <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                           <svg
                              className={`w-4 h-4 ${getThemeClass('text-gray-400', 'text-gray-500')}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth={2}
                                 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                           </svg>
                        </div>
                        <input
                           ref={searchRef}
                           type="text"
                           onKeyDown={handleKeyNavigation}
                           className={`w-full pl-10 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${getThemeClass(
                              'border-gray-300 bg-white text-gray-900 placeholder-gray-500',
                              'border-gray-600 bg-gray-700 text-white placeholder-gray-400',
                           )}`}
                           placeholder={placeholder}
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           onClick={(e) => e.stopPropagation()}
                        />
                        {searchTerm && (
                           <button
                              type="button"
                              className="absolute inset-y-0 right-0 flex items-center pr-3"
                              onClick={() => setSearchTerm('')}>
                              <svg
                                 className={`w-4 h-4 ${getThemeClass(
                                    'text-gray-400 hover:text-gray-600',
                                    'text-gray-500 hover:text-gray-300',
                                 )}`}
                                 fill="none"
                                 stroke="currentColor"
                                 viewBox="0 0 24 24">
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                 />
                              </svg>
                           </button>
                        )}
                     </div>
                  </div>

                  {/* Options List */}
                  <div className="max-h-65 overflow-hidden">
                     {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                           <div
                              className={`animate-spin rounded-full h-6 w-6 border-b-2 ${getThemeClass(
                                 'border-blue-600',
                                 'border-blue-400',
                              )}`}></div>
                        </div>
                     ) : enhancedOptions.length <= 1 ? (
                        <div className="text-center py-4 px-2">
                           <svg
                              className={`w-12 h-12 mx-auto mb-2 ${getThemeClass('text-gray-400', 'text-gray-500')}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth={1}
                                 d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                           </svg>
                           <p className={`text-sm mb-3 ${getThemeClass('text-gray-500', 'text-gray-400')}`}>
                              {searchTerm.trim()
                                 ? `No options found for "${searchTerm.trim()}"`
                                 : 'No options available'}
                           </p>
                           {showAddButton && onAddNew && searchTerm.trim() && user && !user.isExpired && (
                              <button
                                 type="button"
                                 className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white focus:outline-none focus:ring-2 transition-colors ${getThemeClass(
                                    'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 focus:ring-offset-2',
                                    'bg-blue-500 hover:bg-blue-600 focus:ring-blue-400 focus:ring-offset-gray-800',
                                 )}`}
                                 onClick={() => {
                                    handleAddNew(searchTerm);
                                    onAddNew(searchTerm.trim());
                                    setIsOpen(false);
                                    setSearchTerm('');
                                 }}>
                                 <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M12 4v16m8-8H4"
                                    />
                                 </svg>
                                 Add New {label}
                              </button>
                           )}
                        </div>
                     ) : (
                        <List
                           ref={listRef}
                           height={Math.min(MENU_MAX_HEIGHT, enhancedOptions.length * OPTION_HEIGHT)}
                           itemCount={enhancedOptions.length}
                           itemSize={OPTION_HEIGHT}
                           itemData={enhancedOptions}
                           onScroll={handleScroll}>
                           {Option}
                        </List>
                     )}
                  </div>

                  {/* Footer with selection info */}
                  {!isLoading && enhancedOptions.length > 0 && (
                     <div
                        className={`px-3 py-2 border-t ${getThemeClass(
                           'border-gray-200 bg-gray-50 text-gray-500',
                           'border-gray-600 bg-gray-700 text-gray-400',
                        )}`}>
                        <div className="flex items-center justify-between text-xs">
                           <span>
                              {value.length} of {filteredOptions.length} selected
                              {searchTerm && ` (filtered from ${useData.length})`}
                           </span>
                           {loadedCount < filteredOptions.length && (
                              <span className="flex items-center">
                                 <svg
                                    className={`w-3 h-3 mr-1 ${getThemeClass('text-gray-400', 'text-gray-500')}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M19 9l-7 7-7-7"
                                    />
                                 </svg>
                                 Showing {loadedCount} of {filteredOptions.length}
                              </span>
                           )}
                        </div>
                     </div>
                  )}
               </div>
            )}
         </div>

         {/* Item Form Modal */}
         {ItemFormComponent && isFormOpen && (
            <ItemFormComponent
               isOpen={isFormOpen}
               onClose={handleFormClose}
               onSave={handleSave}
               isEditMode={isEditMode}
               demoname={demoname}
               currentRow={currentRow}
               saveandcontinue={false}
               {...formProps}
            />
         )}

         {/* Legacy AddEditFormComponent support */}
         {AddEditFormComponent && isFormOpen && (
            <AddEditFormComponent
               isOpen={isFormOpen}
               onClose={handleFormClose}
               isEditMode={isEditMode}
               demoname={demoname}
               currentRow={currentRow}
               initialData={currentRow}
               onSave={handleFormSave}
               saveandcontinue={false}
            />
         )}
      </>
   );
};

export default MultiSelectDropdown;
